/** El documento del artículo, ABIERTO. No un enlace: el documento.
 *
 * Un usuario de Essity lo pidió dos veces y la segunda fue más clara: «quiere ver el pdf, no
 * texto cuando pide que le muestre el spec sheet». Para quien conoce la ficha de memoria, un
 * resumen correcto no sustituye la firma visual del documento — reconoce la página antes de
 * leerla. El resumen y la tabla siguen debajo, porque son lo citable y lo que respalda las
 * cifras; lo que cambia es qué va primero.
 *
 * Después matti pidió lo mismo para el informe de calidad de la planta, y de ahí sale la forma
 * de aquí: el visor ya no es «de la ficha» sino de DOCUMENTOS, cada uno con su tipo. Una tabla
 * de «spec contra calidad de planta» puede abrir los dos, y entonces la pestaña tiene que decir
 * cuál es cuál — el nombre del artículo se repite en ambas.
 *
 * Quién decide abrirlo: el MODELO, con `mostrar_ficha` y `mostrar_informe`. «Show me the fact
 * sheet» y «what's the caliper?» llaman a la misma operación y esperan cosas distintas, y esa
 * diferencia es intención, no sintaxis. Por eso no hay enrutador: el modelo ya la entiende.
 */
import { useState } from "react";
import { t } from "../lib/i18n";
import type { Idioma } from "../lib/tipos";

export type TipoDocumento = "ficha" | "informe";
export type Documento = { tipo: TipoDocumento; sku: string };

export function clave(d: Documento): string {
  return `${d.tipo}:${d.sku}`;
}

export function VisorDocumento({ docs, nombres, idioma }: {
  docs: Documento[];
  nombres: Record<string, string>;
  idioma: Idioma;
}) {
  const [activo, setActivo] = useState(clave(docs[0]!));
  const txt = t(idioma);
  const doc = docs.find((d) => clave(d) === activo) ?? docs[0]!;
  const titulo = (d: TipoDocumento) => (d === "ficha" ? txt.fichaVisor : txt.informeVisor);
  // El tipo solo se nombra en la pestaña cuando hay más de uno: con un único documento, decir
  // «Just1 · Ficha de PLM» repite lo que ya dice la tarjeta entera.
  const dosTipos = new Set(docs.map((d) => d.tipo)).size > 1;
  const etiqueta = (d: Documento) =>
    (nombres[d.sku] || d.sku) + (dosTipos ? ` · ${titulo(d.tipo)}` : "");
  const url = (d: Documento) => `/api/${d.tipo}/${encodeURIComponent(d.sku)}`;

  return (
    <figure className="visor-ficha">
      {docs.length > 1 && (
        <div className="visor-pestanas" role="tablist" aria-label={titulo(doc.tipo)}>
          {docs.map((d) => (
            <button key={clave(d)} type="button" role="tab" aria-selected={clave(d) === clave(doc)}
                    className={clave(d) === clave(doc) ? "activa" : undefined}
                    onClick={() => setActivo(clave(d))}>
              {etiqueta(d)}
            </button>
          ))}
        </div>
      )}

      {/* `#view=FitH` encaja el ancho en los visores nativos; el `key` fuerza recarga al cambiar
          de pestaña en vez de dejar el documento anterior pintado.
          La carga NO se difiere, y es deliberado: este visor solo existe cuando el usuario pidió
          ver el documento, así que diferirlo hasta que el marco entre en la vista le enseña un
          hueco gris justo donde esperaba la ficha. Medido en producción con el atributo puesto:
          el marco estaba vacío hasta que se hacía scroll. */}
      <iframe key={clave(doc)} className="visor-marco" src={`${url(doc)}#view=FitH`}
              title={`${titulo(doc.tipo)}: ${nombres[doc.sku] || doc.sku}`} />

      {/* La red de seguridad, y no es teórica: Safari en iOS no pinta PDFs dentro de un `iframe`,
          así que sin esto el usuario que más pidió el documento se queda con un hueco gris. */}
      <figcaption>
        <a href={url(doc)} target="_blank" rel="noopener noreferrer">
          {txt.fichaNuevaPestana}
        </a>
      </figcaption>
    </figure>
  );
}
